import IpcRouter from '@shared/ipc/IpcRouter.ts'
import type {
  FileGrantMode,
  FileGrantPathKind,
  FileGrantSummary,
  FileSandboxSummary,
} from '@shared/file-grants/file-grant-types'

export interface FileSandboxIdPayload {
  sandboxId: string
}

export interface CreateFileSandboxPayload extends FileSandboxIdPayload {
  idleStopMs?: number
}

export interface GrantSandboxPathPayload extends FileSandboxIdPayload {
  mode: FileGrantMode
  /** Limits the picker to files or to folders. It offers both when absent. */
  pick?: FileGrantPathKind
  maxBytesWritten?: number
  /**
   * Regrants a path the chat had before (after a restart, say). The picker opens on it, and picking anything else
   * fails with `grant_path_mismatch`, because the chat was told about this path.
   */
  reattachPath?: string
}

export interface AttachFilesPayload {
  /** Where files that can't be uploaded are granted. Without one, those picks come back `failed`. */
  sandboxId?: string
  /** Picks past this many are dropped: the composer's remaining attachment slots. */
  limit: number
}

/** A picked file: its bytes when the chat can upload it (see `isFileUploadable`), otherwise a read-only grant to it. */
export type AttachedFile =
  | { type: 'upload'; name: string; mimeType: string; bytes: Uint8Array }
  | { type: 'grant'; grant: FileGrantSummary }
  /** `code` is a `FileGrantErrorCode`, or `sandbox_not_found` when no `sandboxId` was given. */
  | { type: 'failed'; name: string; code: string }

export interface RevokeSandboxPathPayload extends FileSandboxIdPayload {
  grantId: string
}

export interface FileSandboxPathPayload extends FileSandboxIdPayload {
  path: string
}

export interface ReadFileSandboxRangePayload extends FileSandboxPathPayload {
  position: number
  length: number
}

export interface WriteFileSandboxTextPayload extends FileSandboxPathPayload {
  text: string
}

export interface FileAttachSupport {
  /** Whether chats can use attached files on this computer: the `files` client toolset is on and the OS supports it. */
  isAvailable: boolean
  /** The name this computer shows on attachments, so other computers can say where a file lives. */
  deviceName: string
}

export interface SaveScratchFilePayload {
  conversationId: string
  /** Relative to the chat's scratch folder, as the agent's `offer_download` result gives it. */
  relativePath: string
}

/**
 * Product routes for sandboxed file grants: managing sandboxes and granting or revoking paths through the picker that
 * runs in main. Direct filesystem access by sandbox id (browse, read, write) and the escape probe live on the
 * `file-grants-debug` router, which only unpackaged builds register. Those actions take a path and a sandbox id with
 * no caller identity, so they must never ship in production. `saveScratchFile` is the one path-taking route here: it
 * only copies a file out of a chat's scratch folder into Downloads, which the user clicked to do.
 */
export interface FileGrantsIpcRouterType {
  listSandboxes: () => Promise<Array<FileSandboxSummary>>
  /** The sandbox's grants, or none when it doesn't exist. */
  listGrants: (payload: FileSandboxIdPayload) => Promise<Array<FileGrantSummary>>
  describeAttachSupport: () => Promise<FileAttachSupport>
  createSandbox: (payload: CreateFileSandboxPayload) => Promise<FileSandboxSummary>
  deleteSandbox: (payload: FileSandboxIdPayload) => Promise<void>
  /** Opens the file and folder picker, creating the sandbox if needed. Resolves to `null` when the user cancels. */
  grantPath: (payload: GrantSandboxPathPayload) => Promise<FileGrantSummary | null>
  /** Opens the file picker. Resolves to no files when the user cancels. */
  attachFiles: (payload: AttachFilesPayload) => Promise<Array<AttachedFile>>
  revokePath: (payload: RevokeSandboxPathPayload) => Promise<void>
  /** Copies a file the agent offered into Downloads and shows it in Finder. Resolves to the copy's path. */
  saveScratchFile: (payload: SaveScratchFilePayload) => Promise<string>
}

class FileGrantsIpcRouter extends IpcRouter<FileGrantsIpcRouterType> {
  constructor() {
    super('file-grants', {
      listSandboxes: true,
      listGrants: true,
      describeAttachSupport: true,
      createSandbox: true,
      deleteSandbox: true,
      grantPath: true,
      attachFiles: true,
      revokePath: true,
      saveScratchFile: true,
    })
  }
}

export default FileGrantsIpcRouter
